import React from 'react';
import { sortBy } from 'lodash';
import PropTypes from 'prop-types';
import {
  makeStyles,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Checkbox,
  Typography,
  InputAdornment,
  OutlinedInput,
  Grid,
  Card,
  CardContent,
} from '@material-ui/core';
import Skeleton from '@material-ui/lab/Skeleton';

import EditEventDataField from '../../../../../../widgets/EditEventDataField';
import styles from './styles';

const useStyles = makeStyles(styles);

export const DefaultsFormComponent = (props) => {
  const {
    eventTemplate,
    handleChangeEventDataDefaultValues,
    handleChangeDueDateDefaultTime,
    handleChangeDueDateWorkingTime,
    isLoading,
  } = props;
  const classes = useStyles();

  const defaultDueDateDays = Math.floor(eventTemplate.dueDateDefaultTime / 8.64e7 || 0);
  const defaultDueDateHours = Math.floor((eventTemplate.dueDateDefaultTime % 8.64e7) / 3.6e6 || 0);
  const defaultDueDateMinutes = Math.floor(Math.round((eventTemplate.dueDateDefaultTime % 3.6e6) / 6e4) || 0);

  const handleDueDateChange = (days = 0, hours = 0, minutes = 0) => {
    const newValue = (86400000 * days) + (3600000 * hours) + (60000 * minutes);
    handleChangeDueDateDefaultTime(newValue < 0
      ? 0
      : newValue);
  };

  const handleDueDateWorkingTimeChange = (e) => {
    handleChangeDueDateWorkingTime({
      ...eventTemplate.dueDateWorkingTime,
      [e.target.name]: e.target.checked,
    });
  };

  const handleEventDataChange = (newValue, index) => {
    const eventDataList = sortBy(eventTemplate.eventData, ['order', 'name']);
    handleChangeEventDataDefaultValues({
      ...eventTemplate.eventDataDefaultValues,
      [eventDataList[index].id]: newValue,
    });
  };

  const label = (
    <Grid item xs={12} md={3} lg={2}>
      <Typography variant="subtitle2">Defaults</Typography>
      <Typography variant="caption" gutterBottom>
        Configure due dates and give default values for custom fields.
      </Typography>
    </Grid>
  );

  if (isLoading) {
    return (
      <Grid container spacing={4}>
        {label}
        <Grid item xs={12} md={9} lg={10} container className={classes.section}>
          <Card className={classes.card}>
            <CardContent>
              <Skeleton />
              <Skeleton />
              <Skeleton />
            </CardContent>
          </Card>
          <Card className={classes.card}>
            <CardContent>
              <Skeleton />
              <Skeleton />
              <Skeleton />
            </CardContent>
          </Card>
          <Card className={classes.card}>
            <CardContent>
              <Skeleton />
              <Skeleton />
              <Skeleton />
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container spacing={4}>
      {label}
      <Grid item xs={12} md={9} lg={10} container className={classes.section}>
        <Card className={classes.card}>
          <CardContent>
            <Typography variant="subtitle1">Default Due Date</Typography>
            <Typography variant="caption" component="div" gutterBottom>
              Specify the amount of time after workflow submission until this workflow is due for completion.
            </Typography>
            <Typography variant="caption" component="div" gutterBottom>
              Example: Set the due date for 2 days after the submission of this workflow.
            </Typography>
            <div>

              <FormControl variant="outlined" className={classes.defaultDueDateTimeInput}>
                <OutlinedInput
                  id="default-due-date-days"
                  type="number"
                  margin="dense"
                  value={defaultDueDateDays}
                  onChange={(e) => { handleDueDateChange(e.target.value, defaultDueDateHours, defaultDueDateMinutes); }}
                  endAdornment={<InputAdornment position="end">days</InputAdornment>}
                />
              </FormControl>
              <FormControl variant="outlined" className={classes.defaultDueDateTimeInput}>
                <OutlinedInput
                  id="default-due-date-hours"
                  type="number"
                  margin="dense"
                  value={defaultDueDateHours}
                  onChange={(e) => { handleDueDateChange(defaultDueDateDays, e.target.value, defaultDueDateMinutes); }}
                  endAdornment={<InputAdornment position="end">hours</InputAdornment>}
                />
              </FormControl>
              <FormControl variant="outlined" className={classes.defaultDueDateTimeInput}>
                <OutlinedInput
                  id="default-due-date-minutes"
                  type="number"
                  margin="dense"
                  value={defaultDueDateMinutes}
                  onChange={(e) => { handleDueDateChange(defaultDueDateDays, defaultDueDateHours, e.target.value); }}
                  endAdornment={<InputAdornment position="end">minutes</InputAdornment>}
                />
              </FormControl>
            </div>
            <FormControl fullWidth component="fieldset" className={classes.dueDateWorkingTimeFormControl}>
              <FormLabel>Days included in due date calculation</FormLabel>
              <FormGroup row>
                <FormControlLabel
                  control={<Checkbox checked={!eventTemplate.dueDateWorkingTime || eventTemplate.dueDateWorkingTime.MONDAY !== false} onChange={handleDueDateWorkingTimeChange} name="MONDAY" />}
                  label="Mon"
                />
                <FormControlLabel
                  control={<Checkbox checked={!eventTemplate.dueDateWorkingTime || eventTemplate.dueDateWorkingTime.TUESDAY !== false} onChange={handleDueDateWorkingTimeChange} name="TUESDAY" />}
                  label="Tue"
                />
                <FormControlLabel
                  control={<Checkbox checked={!eventTemplate.dueDateWorkingTime || !eventTemplate.dueDateWorkingTime.WEDNESDAY !== false} onChange={handleDueDateWorkingTimeChange} name="WEDNESDAY" />}
                  label="Wed"
                />
                <FormControlLabel
                  control={<Checkbox checked={!eventTemplate.dueDateWorkingTime || eventTemplate.dueDateWorkingTime.THURSDAY !== false} onChange={handleDueDateWorkingTimeChange} name="THURSDAY" />}
                  label="Thu"
                />
                <FormControlLabel
                  control={<Checkbox checked={!eventTemplate.dueDateWorkingTime || eventTemplate.dueDateWorkingTime.FRIDAY !== false} onChange={handleDueDateWorkingTimeChange} name="FRIDAY" />}
                  label="Fri"
                />
                <FormControlLabel
                  control={<Checkbox checked={!eventTemplate.dueDateWorkingTime || eventTemplate.dueDateWorkingTime.SATURDAY !== false} onChange={handleDueDateWorkingTimeChange} name="SATURDAY" />}
                  label="Sat"
                />
                <FormControlLabel
                  control={<Checkbox checked={!eventTemplate.dueDateWorkingTime || eventTemplate.dueDateWorkingTime.SUNDAY !== false} onChange={handleDueDateWorkingTimeChange} name="SUNDAY" />}
                  label="Sun"
                />
              </FormGroup>
            </FormControl>
          </CardContent>
        </Card>

        <Card className={classes.card}>
          <CardContent>
            <Typography variant="subtitle1">Default Custom Field Values</Typography>
            {
              eventTemplate.eventData.length > 0 && (
                <Typography variant="caption" gutterBottom>
                  Specify fields you wish to pre-populate with data when
                  this workflow is used at event creation. Leave blank if you
                  wish to have the field filled out by the user.
                </Typography>
              )
            }
            {
              eventTemplate.eventData.length === 0
                ? (
                  <Typography>
                    No custom fields added to this workflow. Add custom fields above and set their default values here.
                  </Typography>
                )
                : sortBy(eventTemplate.eventData, ['order', 'name']).map((data, index) => (
                  <EditEventDataField
                    key={data.name}
                    eventDataField={{
                      ...data,
                      value: eventTemplate.eventDataDefaultValues[data.id],
                    }}
                    onChange={(value) => handleEventDataChange(value, index)}
                  />
                ))
            }
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

DefaultsFormComponent.propTypes = {
  eventTemplate: PropTypes.object,
  handleChangeEventDataDefaultValues: PropTypes.func,
  handleChangeDueDateDefaultTime: PropTypes.func,
  handleChangeDueDateWorkingTime: PropTypes.func,
  isLoading: PropTypes.bool,
};

DefaultsFormComponent.defaultProps = {
  eventTemplate: {},
  handleChangeEventDataDefaultValues: () => {},
  handleChangeDueDateDefaultTime: () => {},
  handleChangeDueDateWorkingTime: () => {},
  isLoading: true,
};

export default DefaultsFormComponent;
